// ---------------------------------------------------------------------------
// Helpers shared by the editor's "link GIS layer" panels (GisLayerPanel.tsx
// for sub-basins, LinkGisPanel.tsx for aquifers and outlet points,
// Step1SubBasins.tsx in the new-project wizard) — agnostic of the linked
// entity type. Lives directly under ui/ (not editor/ or wizard/) precisely
// because both use it — see project_evalhid_code_audit.md.
// ---------------------------------------------------------------------------

import { exceedsMaxImportSize } from "../model/fileSizeLimits.ts";
import { parseGeoJSON, parseShapefile } from "../model/gisLayer.ts";
import { TranslatableError } from "../i18n/translate.ts";
import type { Translate } from "../i18n/hooks.ts";

/** Groups the loose files of an `<input type="file" multiple>` by extension (shp/dbf/prj...). */
async function groupByExtension(files: FileList): Promise<Map<string, File>> {
  const byExt = new Map<string, File>();
  for (const f of Array.from(files)) {
    const ext = f.name.toLowerCase().match(/\.(\w+)$/)?.[1];
    if (ext) byExt.set(ext, f);
  }
  return byExt;
}

interface UploadedLayer {
  geojson: GeoJSON.FeatureCollection;
  /** Non-blocking notice (e.g. missing .dbf or .prj) — the geojson is still usable either way. */
  notice: string | null;
}

/**
 * Groups and parses the files of an `<input type="file" multiple>`, accepting
 * either a shapefile (.shp + optional .dbf/.prj) or a .geojson/.json already
 * in WGS84. The only strictly required file is the .shp or the .geojson/
 * .json; if a shapefile's .dbf or .prj is missing, it's still processed
 * (only attributes or projection are lost) but a notice is returned to show
 * in the UI.
 */
export async function loadGisFiles(files: FileList, t: Translate): Promise<UploadedLayer> {
  const byExt = await groupByExtension(files);
  const oversized = Array.from(byExt.values()).find(exceedsMaxImportSize);
  if (oversized) throw new TranslatableError("gisUpload.fileTooLarge", { name: oversized.name });

  const geojsonFile = byExt.get("geojson") ?? byExt.get("json");
  if (geojsonFile) {
    const geojson = parseGeoJSON(await geojsonFile.text());
    if (geojson.features.length === 0) throw new TranslatableError("gisUpload.noGeometries");
    return { geojson, notice: null };
  }

  const shp = byExt.get("shp");
  if (!shp) throw new TranslatableError("gisUpload.missingShp");
  const dbf = byExt.get("dbf");
  const prj = byExt.get("prj");
  const [shpBuf, dbfBuf, prjText] = await Promise.all([
    shp.arrayBuffer(),
    dbf ? dbf.arrayBuffer() : Promise.resolve(null),
    prj ? prj.text() : Promise.resolve(null),
  ]);
  const geojson = parseShapefile(shpBuf, dbfBuf, prjText);
  if (geojson.features.length === 0) throw new TranslatableError("gisUpload.shapefileNoGeometries");

  const notices: string[] = [];
  if (!dbf) notices.push(t("gisUpload.missingDbf"));
  if (!prj) notices.push(t("gisUpload.missingPrj"));
  return { geojson, notice: notices.length > 0 ? notices.join(" ") : null };
}

/**
 * Chooses the initial link column: the one that actually links the most
 * entities (testing each candidate column with `countLinked`) — a
 * name-based heuristic isn't enough, because for aquifers and outlet points
 * created by hand (without `gisCode`) a numeric id column never links
 * anything, and the "Link layer" button would stay silently disabled until
 * the user changes the column by hand. If no column links anything (a
 * freshly uploaded file with nothing to link yet), falls back to the same
 * heuristic as before as a reasonable tie-breaker.
 */
export function chooseInitialIdField(
  numericFields: string[],
  textFields: string[],
  countLinked: (fieldId: string) => number
): string {
  const candidates = [...numericFields, ...textFields];
  if (candidates.length === 0) return "";
  const heuristic = numericFields.find((c) => /id.*evalhid|evalhid.*id/i.test(c)) ?? candidates[0];
  let best = heuristic;
  let bestCount = countLinked(heuristic);
  for (const c of candidates) {
    if (c === best) continue;
    const count = countLinked(c);
    if (count > bestCount) {
      best = c;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The same already-loaded layer (sub-basins, aquifers or outlet points), as
 * plain GeoJSON — keeps geometry and ALL the raw attributes from the
 * original .dbf (`properties.attributes`, present in all 3 forms of
 * `*Layer.features`), so it can be used to try another link column without
 * having to re-upload the shapefile.
 */
export function geojsonFromLayerWithAttributes(layer: {
  features: { geometry: GeoJSON.Geometry; properties: { attributes: GeoJSON.GeoJsonProperties } }[];
}): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: layer.features.map((f) => ({
      type: "Feature",
      properties: f.properties.attributes,
      geometry: f.geometry,
    })),
  };
}
